import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertCircle, Check, Loader2, Mail } from 'lucide-react'
import { Alert, AlertDescription } from '@/components/ui/alert.tsx'
import { Button } from '@/components/ui/button.tsx'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card.tsx'
import { Input } from '@/components/ui/input.tsx'

export function WebinarRecoveryPage() {
  const { t } = useTranslation()
  const [email, setEmail] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!email.trim()) {
      setError(t('validation.emailRequired'))
      return
    }

    setIsSubmitting(true)
    setError(null)

    try {
      const res = await fetch('/api/webinar/recover', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: email.trim() }),
      })
      const data = await res.json()

      if (!res.ok || data.error) {
        throw new Error(data.error?.message || t('webinar.recover.error'))
      }

      setSuccess(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('webinar.recover.error'))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#E8E4F0] to-[#D6E5F0] p-4 sm:p-8">
      <div className="w-full max-w-md">
        {success ? (
          <Card>
            <CardHeader className="text-center">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 rounded-full bg-[#63C36F]/10 flex items-center justify-center">
                  <Check className="w-8 h-8 text-[#63C36F]" />
                </div>
              </div>
              <CardTitle>{t('webinar.recover.successTitle')}</CardTitle>
              <CardDescription>{t('webinar.recover.successDescription')}</CardDescription>
            </CardHeader>
          </Card>
        ) : (
          <Card>
            <CardHeader className="text-center">
              <Mail className="w-10 h-10 text-[#26247B] mx-auto mb-2" />
              <CardTitle>{t('webinar.recover.title')}</CardTitle>
              <CardDescription>{t('webinar.recover.description')}</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-[#363636] mb-1">
                    {t('common.email')}
                  </label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t('auth.signIn.emailPlaceholder')}
                    required
                  />
                </div>

                {error && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <Button
                  type="submit"
                  className="w-full py-4 bg-[#26247B] hover:bg-[#1a1860] text-white font-medium rounded-lg"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {t('common.loading')}
                    </span>
                  ) : (
                    t('webinar.recover.submit')
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  )
}
